import { useState } from "react";

import { format, isValid } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@utils/ui";

import { Button } from "../ui/button";

type CalendarProps = {
  currentDate?: Date | null;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  handleDateSelect: (date: Date) => void;
};

export const Calendar = ({
  currentDate,
  dateFormat = "yyyy-MM-dd",
  minDate,
  maxDate,
  handleDateSelect,
}: CalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Initialize to the month of the current date if it exists and is valid
    if (currentDate && isValid(currentDate)) {
      return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    }
    return new Date();
  });

  const isValidDate = currentDate && isValid(currentDate);

  // Navigation for calendar
  const navigateMonth = (direction: "prev" | "next") => {
    const newMonth = new Date(currentMonth);
    if (direction === "prev") {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  // Generate calendar days
  const generateCalendarDays = (): Date[] => {
    const firstDay = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: Date[] = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthYear = format(currentMonth, "MMMM yyyy");

  // Check if date is disabled
  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  // Check if date is selected
  const isDateSelected = (date: Date) => {
    return (
      isValidDate &&
      currentDate &&
      format(date, dateFormat) === format(currentDate, dateFormat)
    );
  };

  // Check if date is in current month
  const isInCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  return (
    <div className="p-3">
      {/* Calendar Header */}
      <div className="mb-4 flex items-center justify-between">
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => navigateMonth("prev")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm font-semibold">{monthYear}</div>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => navigateMonth("next")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day headers */}
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div
            key={day}
            className="text-muted-foreground flex h-9 w-9 items-center justify-center text-center text-sm font-medium"
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {calendarDays.map((date, index) => {
          const disabled = isDateDisabled(date);
          const selected = isDateSelected(date);
          const inCurrentMonth = isInCurrentMonth(date);

          return (
            <Button
              key={index}
              variant={selected ? "default" : "ghost"}
              className={cn(
                "h-9 w-9 p-0 text-sm",
                !inCurrentMonth && "text-muted-foreground opacity-50",
                selected &&
                  "bg-primary text-primary-foreground hover:bg-primary",
                disabled && "cursor-not-allowed opacity-50"
              )}
              disabled={disabled}
              onClick={() => !disabled && handleDateSelect(date)}
            >
              {date.getDate()}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
